import { ColumnDef } from '@tanstack/react-table';
import {
  formatTableDate,
  formatTableTimestamp,
} from '../../utils/formatTableDate';
import {
  SortableHeader,
  TruncatedCell,
  DownloadedCheckmark,
} from '@/components';
import { ProviderNoteRecord } from '../types';
import { PatientDocumentViewButton } from '../../components';
import { DocumentSource, DocumentType } from '../../viewer';
import { DASH } from '../../utils/displayValues';

export const createProviderNotesColumns =
  (): ColumnDef<ProviderNoteRecord>[] => [
    {
      id: 'documentName',
      header: 'Document Name',
      cell: ({ row }) => {
        if (row.original.title === null) return DASH;
        const documentName = row.original.title || row.original.comments;
        return documentName ? (
          <TruncatedCell text={documentName} />
        ) : (
          'No data available'
        );
      },
    },
    {
      accessorKey: 'facilityType',
      header: 'Facility Type',
      cell: ({ row }) => {
        const label =
          row.original.facilityType ?? row.original.alternateTypeName;
        if (label) return <TruncatedCell text={label} />;
        return row.original.facilityType === null ? DASH : 'No data available';
      },
    },
    {
      id: 'dateOfStudy', // backend sort key; accessor maps to the local data field
      accessorKey: 'dateOfServiceStart',
      enableSorting: true,
      header: ({ column }) => (
        <SortableHeader column={column}>Date of Service</SortableHeader>
      ),
      cell: ({ row }) => formatTableDate(row.original.dateOfServiceStart),
    },
    {
      accessorKey: 'providerName',
      header: 'Provider Name',
      cell: ({ row }) => (
        <TruncatedCell
          text={
            row.original.providerName === null
              ? DASH
              : row.original.providerName || 'No data available'
          }
        />
      ),
    },
    {
      id: 'dateUploaded',
      accessorFn: (row) => row.createdAt,
      enableSorting: true,
      header: ({ column }) => (
        <SortableHeader column={column}>Date Uploaded</SortableHeader>
      ),
      cell: ({ row }) => formatTableTimestamp(row.original.createdAt),
    },
    {
      accessorKey: 'sourceType',
      enableSorting: true,
      header: ({ column }) => (
        <SortableHeader column={column}>Source Type</SortableHeader>
      ),
      cell: ({ row }) => row.original.sourceType || 'No data available',
    },
    {
      id: 'markedDownloaded',
      enableSorting: false,
      header: '',
      cell: ({ row }) =>
        row.original.markedDownloaded ? <DownloadedCheckmark /> : null,
    },
    {
      id: 'actions',
      enableSorting: false,
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <PatientDocumentViewButton
            disabled={!row.original.sourceType}
            documentKey={{
              source: DocumentSource.MEDICAL_RECORDS,
              type: DocumentType.PROVIDER_NOTES,
              providerNoteId: row.original.id,
              sourceType: row.original.sourceType,
              name:
                row.original.title === null
                  ? DASH
                  : row.original.title || row.original.comments,
            }}
          />
        </div>
      ),
    },
  ];
